/* Établissements — ON-03/04/05/13/18 (Bible §19/§47/§58 · locked #6).
   Location creation resolves the REAL Google listing before save — never a
   silent attach. Three Google capacities shown separately: link, reporting,
   response. Archive preserves attribution and issued-Gift terms. */
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Button, Card, EmptyState, Field, Icon, Input, KV, List, ListRow, Note, PageHeader, Pill, Select, Skeleton, useToast } from '@/ui'
import { useLocations } from '@/lib/api'
import { ALL, useScope } from '@/lib/scope'
import { getPlan } from '@/lib/campaign'
import { eur, extraLocationAmount, planLocations, planOffer } from '@/lib/plans'
import { EtablissementPage } from './Etablissement'

type Vue = 'liste' | 'resolveur' | 'detail' | 'manuel'

export default function Etablissements() {
  const { data, isPending } = useLocations()
  const [params] = useSearchParams()
  const [scope, setScope] = useScope()
  /* ?ajouter=1 — the switcher's « + Ajouter un établissement » lands on the resolver */
  const [vue, setVue] = useState<Vue>(params.get('ajouter') ? 'resolveur' : 'liste')
  /* ?etat=vide — the pre-confirmation state (ON-18): the account exists but no
     establishment is confirmed yet. A URL hook because every world’s fixture
     rightly carries the signup Location (backend convention, handoff §5.2). */
  const vide = params.get('etat') === 'vide'
  const [url, setUrl] = useState('')
  const [found, setFound] = useState(false)
  const [justAdded, setJustAdded] = useState(false) // Batch 2 — land on "what's next"
  const toast = useToast()
  const nav = useNavigate()

  if (isPending) return <div style={{ display: 'grid', gap: 10, maxWidth: 720 }}><Skeleton width="42%" /><Skeleton /><Skeleton width="65%" /></div>
  const list = data ?? []

  const retour = <p className="f-help" style={{ margin: 0 }}>
    <a href="#" onClick={e => { e.preventDefault(); setVue('liste') }}>‹ Établissements</a></p>

  /* ---------- ON-03 · the Google listing resolver ---------- */
  if (vue === 'resolveur') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 680 }}>
      {retour}
      <h2 className="t-page" style={{ margin: 0 }}>Ajouter votre établissement</h2>
      <Field label="Collez le lien de votre fiche Google — ou cherchez votre établissement">
        {id => <Input id={id} value={url} placeholder="https://maps.google.com/…"
          onChange={e => { setUrl(e.target.value); setFound(e.target.value.length > 8) }} />}
      </Field>
      {found && <>
        <Note tone="ok">Fiche trouvée&nbsp;: <b>Valessio — Coiffeur Paris 9</b> · 17 rue de Châteaudun, 75009 Paris · 4,6&nbsp;★ (212 avis) · lien d’avis public détecté</Note>
        <div style={{ display: 'flex', gap: 10 }}>
          <Button variant="primary" onClick={() => {
            setVue('liste'); setJustAdded(true)
            toast({ message: '✓ Fiche Google rattachée — lien d’avis vérifié.' })
          }}>C’est bien mon établissement</Button>
          <Button onClick={() => setFound(false)}>Ce n’est pas le bon</Button>
        </div>
      </>}
      <p className="f-help">Correspondance ambiguë&nbsp;? Nous ne rattachons jamais une fiche sans votre confirmation explicite.</p>
      <p className="f-help">Sans fiche Google&nbsp;: <a href="#" onClick={e => { e.preventDefault(); setVue('manuel') }}>saisir les informations manuellement</a></p>
    </div>
  )

  /* ---------- ON-13 · manual location — honest about what it excludes ---------- */
  if (vue === 'manuel') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 620 }}>
      {retour}
      <h2 className="t-page" style={{ margin: 0 }}>Ajouter l’établissement manuellement</h2>
      <Card>
        <div style={{ display: 'grid', gap: 12 }}>
          <div className="fields-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <Field label="Nom">{id => <Input id={id} placeholder="Valessio — Paris 9" />}</Field>
            <Field label="Téléphone">{id => <Input id={id} placeholder="01 …" />}</Field>
          </div>
          <Field label="Adresse">{id => <Input id={id} placeholder="17 rue de Châteaudun, 75009 Paris" />}</Field>
          <div className="fields-row" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <Field label="Fuseau horaire">{id => <Select id={id}><option>Europe/Paris</option></Select>}</Field>
            <Field label="Secteur">{id => <Select id={id}><option>Coiffure & beauté</option><option>Restauration</option><option>Autre</option></Select>}</Field>
          </div>
        </div>
      </Card>
      <Note tone="candour">Sans fiche Google&nbsp;: pas de destination Google ni de rapport de note — tout le reste fonctionne. Vous pourrez lier une fiche plus tard.</Note>
      <div><Button variant="primary" onClick={() => {
        setVue('liste')
        toast({ message: '✓ Établissement créé — vous pourrez lier une fiche Google plus tard.' })
      }}>Créer l’établissement</Button></div>
    </div>
  )

  /* ---------- ON-05 · location detail — the three Google capacities ---------- */
  if (vue === 'detail') return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 720 }}>
      {retour}
      <h2 className="t-page" style={{ margin: 0 }}>Valessio — Paris 9</h2>
      <div style={{ display: 'flex', gap: 6 }}>
        {['Infos', 'Google', 'Équipe', 'Cadeaux locaux', 'Style local'].map((t, i) => (
          <Button key={t} size="sm" variant={i === 1 ? 'primary' : 'quiet'}>{t}</Button>
        ))}
      </div>
      <Card>
        <KV rows={[
          ['Lien d’avis public', <span key="1"><Pill tone="ok">Vérifié</Pill> ouvre le formulaire Google</span>],
          ['Rapport note & nombre d’avis', <span key="2"><Pill tone="ok">Connecté</Pill> instantané quotidien 06:00</span>],
          ['Réponses aux avis (IA)', <span key="3"><Pill tone="warn">En attente d’accès Google</Pill></span>],
          ['Appareils équipe', <span key="4">2 actifs · <a href="#" onClick={e => e.preventDefault()}>gérer</a></span>],
          ['Fuseau & horaires', <span key="5">Europe/Paris · <a href="#" onClick={e => e.preventDefault()}>modifier</a></span>],
        ]} />
      </Card>
      <p className="f-help">Trois capacités distinctes&nbsp;: lien, rapport, réponse. Un lien valide ne donne accès ni aux notes ni aux réponses.</p>
    </div>
  )

  /* ---------- ON-18 · empty — the object defined in the merchant’s terms ---------- */
  if (list.length === 0 || vide) return (
    <EmptyState title="Vos adresses, une par une.">
      <p className="t-body" style={{ maxWidth: 580 }}>Un établissement, c’est un lieu physique avec sa propre fiche Google, sa propre équipe et ses propres cadeaux. Tout ce que vous réglez ensuite — campagnes, supports, statistiques — se rattache à l’un d’eux.</p>
      <p className="t-body" style={{ maxWidth: 580 }}>Si vous n’avez qu’un salon, vous n’en aurez jamais qu’un et cette page restera calme. Si vous en avez plusieurs, c’est ici que vous les comparerez.</p>
      <p className="f-help" style={{ maxWidth: 580 }}>C’est vide parce que votre établissement n’a pas encore été confirmé — c’est la première étape et elle prend une minute avec votre fiche Google.</p>
      <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
        <Button variant="primary" onClick={() => setVue('resolveur')}>Trouver ma fiche Google</Button>
        <Button onClick={() => setVue('manuel')}>Saisir l’adresse à la main</Button>
      </div>
    </EmptyState>
  )

  /* ---------- D-31 · inside a context, Établissements IS the establishment's own page ---------- */
  const inCtx = scope !== ALL ? list.find(l => l.id === scope) : undefined
  if (inCtx && vue === 'liste') return <EtablissementPage loc={inCtx} />

  /* ---------- ON-04 · location list — readiness at a glance ---------- */
  const plan = getPlan()
  const included = planLocations(plan)   // FQ-I4 · plans.js, no Growth
  return (
    <div className="page">
      <PageHeader title="Établissements"
        description="Vos lieux. Ouvrez-en un pour ne voir que lui — ses résultats, ses clients, ses supports, son équipe. Un seul salon ? Cette page reste calme."
        actions={<Button size="sm" variant="primary" onClick={() => setVue('resolveur')}><Icon name="plus" /> Ajouter un établissement</Button>} />
      {/* the post-add hand-off, in the checklist's order (C-04) */}
      {justAdded && (
        <Note tone="ok">Établissement rattaché. <b>Et ensuite&nbsp;:</b>{' '}
          <a href="#" onClick={e => { e.preventDefault(); nav('/identite') }}>votre identité visuelle</a> ·{' '}
          <a href="#" onClick={e => { e.preventDefault(); nav('/cadeaux') }}>vos cadeaux</a> ·{' '}
          <a href="#" onClick={e => { e.preventDefault(); nav('/campagnes/nouvelle') }}>votre campagne</a> ·{' '}
          <a href="#" onClick={e => { e.preventDefault(); nav('/supports') }}>votre support</a>
        </Note>
      )}
      <List>
        {list.map(l => (
          <ListRow key={l.id} icon={<Icon name="locations" size={17} />} title={l.name}
            meta={l.status === 'active' ? `fiche ✓ · rapport ✓ · réponses ◔ en attente${l.rating ? ` · ${l.rating} ★` : ''} · 2 appareils`
              : l.status === 'pending' ? 'fiche ◔ à confirmer · rapport ✕ · réponses ✕ · aucun appareil'
              : 'archivé — historique conservé'}
            end={<div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {l.status === 'active' && <Pill tone="ok">Prêt</Pill>}
              {l.status === 'pending' && <Pill tone="warn">À vérifier</Pill>}
              {l.status === 'inactive' && <Pill tone="done">Archivé</Pill>}
              <Button size="sm" variant="quiet" onClick={() => { setScope(l.id); nav('/') }}>Ouvrir</Button>
            </div>} />
        ))}
      </List>
      <p className="f-help">{planOffer(plan)}&nbsp;: {list.filter(l => l.status !== 'inactive').length} établissement{list.filter(l => l.status !== 'inactive').length > 1 ? 's' : ''} actif{list.filter(l => l.status !== 'inactive').length > 1 ? 's' : ''} sur {included} inclus{plan === 'multi' ? ` (+ ${eur(extraLocationAmount())} par établissement au-delà)` : ''}. Archiver préserve l’attribution, l’accès portefeuille et les conditions des cadeaux émis.</p>
    </div>
  )
}
